import { DashboardLayout } from '@/components/app/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AuthAPI,
  AdminAPI,
  type AdminCreateUserPayload,
  type AdminUpdateUserPayload,
} from '@/services/api'
import { useState, useMemo, useEffect } from 'react'
import {
  Loader2,
  Search,
  UserPlus,
  Mail,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Trash2,
  Users,
  CheckCircle2,
  UsersRound,
  Shield,
} from 'lucide-react'
import { toast } from 'sonner'

const initialCreateForm = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  role: 'PARENT' as 'PARENT' | 'ADMIN',
  isActive: true,
}

const initialEditForm = {
  firstName: '',
  lastName: '',
  email: '',
  isActive: true,
}

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const UserManagement = () => {
  const [user, setUser] = useState<any>(null)
  const [usersList, setUsersList] = useState<any[]>([])
  const [userStats, setUserStats] = useState<any>({ total: 0, active: 0, parents: 0, admins: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState<string | null>(null)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [createForm, setCreateForm] = useState(initialCreateForm)
  const [editForm, setEditForm] = useState(initialEditForm)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 8

  const fetchUsersPayload = async () => {
    const payload = await AdminAPI.getUsers()
    const parentOnlyUsers = (payload.users || []).filter(
      (u) => String(u.role || '').toLowerCase() === 'parent'
    )

    setUsersList(parentOnlyUsers)
    setUserStats({
      total: parentOnlyUsers.length,
      active: parentOnlyUsers.filter((u) => String(u.status || '').toLowerCase() === 'active').length,
      parents: parentOnlyUsers.length,
      admins: 0,
    })
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const u = await AuthAPI.me()
        setUser(u.user)
        await fetchUsersPayload()
      } catch (err) {
        console.error('Failed to fetch users', err)
        toast.error('Failed to fetch users')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  const resetCreateForm = () => {
    setCreateForm(initialCreateForm)
  }

  const resetEditForm = () => {
    setEditForm(initialEditForm)
    setSelectedUser(null)
  }

  const handleCreateUser = async () => {
    if (!createForm.firstName || !createForm.lastName || !createForm.email || !createForm.password) {
      toast.error('Please fill in all required user fields')
      return
    }

    const payload: AdminCreateUserPayload = {
      firstName: createForm.firstName,
      lastName: createForm.lastName,
      email: createForm.email,
      password: createForm.password,
      role: createForm.role,
      isActive: createForm.isActive,
    }

    try {
      setIsCreating(true)
      await AdminAPI.createUser(payload)
      toast.success('User created successfully')
      setIsCreateDialogOpen(false)
      resetCreateForm()
      await fetchUsersPayload()
      setCurrentPage(1)
    } catch (error: any) {
      console.error('Failed to create user', error)
      toast.error(error?.response?.data?.message || 'Failed to create user')
    } finally {
      setIsCreating(false)
    }
  }

  const handleOpenEdit = (targetUser: any) => {
    setSelectedUser(targetUser)
    const names = String(targetUser.name || '').split(' ')
    setEditForm({
      firstName: names[0] || '',
      lastName: names.slice(1).join(' ') || '',
      email: targetUser.email || '',
      isActive: targetUser.status === 'active',
    })
    setIsEditDialogOpen(true)
  }

  const handleUpdateUser = async () => {
    if (!selectedUser?.id) return
    if (!editForm.firstName || !editForm.lastName || !editForm.email) {
      toast.error('Please complete first name, last name, and email')
      return
    }

    const payload: AdminUpdateUserPayload = {
      firstName: editForm.firstName,
      lastName: editForm.lastName,
      email: editForm.email,
      isActive: editForm.isActive,
    }

    try {
      setIsUpdating(true)
      await AdminAPI.updateUser(selectedUser.id, payload)
      toast.success('User updated successfully')
      setIsEditDialogOpen(false)
      resetEditForm()
      await fetchUsersPayload()
    } catch (error: any) {
      console.error('Failed to update user', error)
      toast.error(error?.response?.data?.message || 'Failed to update user')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteUser = async (targetUser: any) => {
    if (!targetUser?.id) return

    try {
      setIsDeleting(targetUser.id)
      await AdminAPI.deleteUser(targetUser.id)
      setUsersList((prev) => {
        return prev.filter((user) => user.id !== targetUser.id)
      })
      setUserStats((prev: { total: number; active: number; parents: number; admins: number }) => ({
        ...prev,
        total: Math.max(prev.total - 1, 0),
        active: Math.max(
          prev.active - (String(targetUser.status || '').toLowerCase() === 'active' ? 1 : 0),
          0
        ),
        parents: Math.max(prev.parents - 1, 0),
        admins: 0,
      }))
      toast.success('User deleted successfully')
      setCurrentPage(1)
      setIsDeleteDialogOpen(false)
      setSelectedUser(null)
    } catch (error: any) {
      console.error('Failed to delete user', error)
      toast.error(error?.response?.data?.message || 'Failed to delete user')
    } finally {
      setIsDeleting(null)
    }
  }

  const filteredUsers = useMemo(() => {
    return usersList.filter((u) => {
      const name = String(u.name || '')
      const email = String(u.email || '')
      const role = String(u.role || '').toLowerCase()
      const status = String(u.status || '').toLowerCase()

      const matchesSearch =
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        email.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesRole = filterRole === 'all' || role === filterRole
      const matchesStatus = filterStatus === 'all' || status === filterStatus

      return matchesSearch && matchesRole && matchesStatus
    })
  }, [searchTerm, filterRole, filterStatus, usersList])

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage)
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filteredUsers.slice(startIndex, endIndex)
  }, [filteredUsers, currentPage])

  // Reset to page 1 when filters change
  const handleFilterChange = () => {
    setCurrentPage(1)
  }

  const getRoleBadge = (role: string) => {
    const variants = {
      admin: 'destructive',
      parent: 'default',
    } as const

    const icons = {
      admin: '🛡️',
      parent: '👤',
    }

    return (
      <Badge variant={variants[role as keyof typeof variants] || 'default'} className="gap-1">
        <span>{icons[role as keyof typeof icons]}</span>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </Badge>
    )
  }

  const getStatusBadge = (status: string) => {
    return (
      <Badge
        variant={status === 'active' ? 'default' : 'secondary'}
        className={
          status === 'active'
            ? 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-950/50 dark:text-green-200 dark:hover:bg-green-950/70'
            : 'bg-muted text-foreground'
        }
      >
        {status === 'active' ? '✓ Active' : '○ Inactive'}
      </Badge>
    )
  }

  if (isLoading) {
    return (
      <DashboardLayout user={user || { firstName: 'Admin', lastName: '', email: '', role: 'ADMIN' }}>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom duration-500">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
              User Management
            </h1>
            <p className="text-muted-foreground mt-1">View and manage system users</p>
          </div>

          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="default" className="gap-2">
                <UserPlus className="h-4 w-4" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>
                  Create a parent or admin account. For parent accounts, you can create an initial child profile.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={createForm.firstName}
                      onChange={(e) => setCreateForm((prev) => ({ ...prev, firstName: e.target.value }))}
                      placeholder="Juan"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={createForm.lastName}
                      onChange={(e) => setCreateForm((prev) => ({ ...prev, lastName: e.target.value }))}
                      placeholder="Dela Cruz"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={createForm.email}
                      onChange={(e) => setCreateForm((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="parent@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={createForm.password}
                      onChange={(e) => setCreateForm((prev) => ({ ...prev, password: e.target.value }))}
                      placeholder="At least 8 characters"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>User Role</Label>
                    <Select
                      value={createForm.role}
                      onValueChange={(value) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          role: value as 'PARENT' | 'ADMIN',
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PARENT">Parent</SelectItem>
                        <SelectItem value="ADMIN">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Account Status</Label>
                    <Select
                      value={createForm.isActive ? 'active' : 'inactive'}
                      onValueChange={(value) =>
                        setCreateForm((prev) => ({ ...prev, isActive: value === 'active' }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreateDialogOpen(false)
                    resetCreateForm()
                  }}
                >
                  Cancel
                </Button>
                <Button variant="default" onClick={handleCreateUser} disabled={isCreating}>
                  {isCreating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create User'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4 animate-in slide-in-from-top duration-700">
          <Card className="border border-blue-500/30 bg-blue-500/10 backdrop-blur-sm dark:border-blue-500/25 dark:bg-blue-950/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Total Users</p>
                  <p className="text-3xl font-bold text-blue-700 dark:text-blue-300">{userStats.total}</p>
                </div>
                <Users className="h-10 w-10 text-blue-600/80 dark:text-blue-400/80" aria-hidden />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-emerald-500/30 bg-emerald-500/10 backdrop-blur-sm dark:border-emerald-500/25 dark:bg-emerald-950/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-900 dark:text-emerald-200">Active Users</p>
                  <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">
                    {userStats.active}
                  </p>
                </div>
                <CheckCircle2 className="h-10 w-10 text-emerald-600/80 dark:text-emerald-400/80" aria-hidden />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-violet-500/30 bg-violet-500/10 backdrop-blur-sm dark:border-violet-500/25 dark:bg-violet-950/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-violet-900 dark:text-violet-200">Parents</p>
                  <p className="text-3xl font-bold text-violet-700 dark:text-violet-300">
                    {userStats.parents}
                  </p>
                </div>
                <UsersRound className="h-10 w-10 text-violet-600/80 dark:text-violet-400/80" aria-hidden />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-amber-500/30 bg-amber-500/10 backdrop-blur-sm dark:border-amber-500/25 dark:bg-amber-950/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-200">Admins</p>
                  <p className="text-3xl font-bold text-amber-700 dark:text-amber-300">
                    {userStats.admins}
                  </p>
                </div>
                <Shield className="h-10 w-10 text-amber-600/80 dark:text-amber-400/80" aria-hidden />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card className="border-transparent bg-muted/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>User Directory</CardTitle>
            <CardDescription>Search and filter users</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value)
                    handleFilterChange()
                  }}
                  className="pl-10"
                />
              </div>
              <Select
                value={filterRole}
                onValueChange={(value) => {
                  setFilterRole(value)
                  handleFilterChange()
                }}
              >
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filterStatus}
                onValueChange={(value) => {
                  setFilterStatus(value)
                  handleFilterChange()
                }}
              >
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="border-transparent bg-muted/30 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>User List</CardTitle>
                <CardDescription>
                  {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
                </CardDescription>
              </div>
              {totalPages > 1 && (
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border border-border bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Children</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="text-right w-[220px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedUsers.map((user, index) => (
                      <TableRow
                        key={user.id}
                        className="hover:bg-accent/40 transition-colors animate-in slide-in-from-bottom"
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center text-lg">
                              {(user.name || 'U').charAt(0)}
                            </div>
                            <div>
                              <div className="font-semibold">{user.name || 'Unknown User'}</div>
                              <div className="text-xs text-muted-foreground">
                                Joined {user.joinedDate ? formatDateTime(user.joinedDate).split(',')[0] : 'N/A'}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{user.email}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getRoleBadge(user.role)}</TableCell>
                        <TableCell>{getStatusBadge(user.status)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            {user.children}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {user.lastLogin ? formatDateTime(user.lastLogin) : 'N/A'}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <div className="inline-flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenEdit(user)}
                              className="h-8 px-3"
                            >
                              <Pencil className="mr-1 h-3.5 w-3.5" />
                              Edit
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user)
                                setIsDeleteDialogOpen(true)
                              }}
                              disabled={isDeleting === user.id}
                              className="h-8 px-3"
                            >
                              <Trash2 className="mr-1 h-3.5 w-3.5" />
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of{' '}
                  {filteredUsers.length} users
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="gap-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      const showPage =
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)

                      if (!showPage) {
                        // Show ellipsis
                        if (page === currentPage - 2 || page === currentPage + 2) {
                          return (
                            <span key={page} className="px-2 text-muted-foreground">
                              ...
                            </span>
                          )
                        }
                        return null
                      }

                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Button>
                      )
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="gap-1"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update parent user information.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editFirstName">First Name</Label>
                  <Input
                    id="editFirstName"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, firstName: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editLastName">Last Name</Label>
                  <Input
                    id="editLastName"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, lastName: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="editEmail">Email</Label>
                <Input
                  id="editEmail"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={editForm.isActive ? 'active' : 'inactive'}
                  onValueChange={(value) =>
                    setEditForm((prev) => ({ ...prev, isActive: value === 'active' }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false)
                  resetEditForm()
                }}
              >
                Cancel
              </Button>
              <Button variant="default" onClick={handleUpdateUser} disabled={isUpdating}>
                {isUpdating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update User'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isDeleteDialogOpen}
          onOpenChange={(open) => {
            setIsDeleteDialogOpen(open)
            if (!open) {
              setSelectedUser(null)
            }
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Delete User</DialogTitle>
              <DialogDescription>
                This will permanently remove the account from the database.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-sm text-muted-foreground">
              <div className="font-semibold text-foreground">
                {selectedUser?.name || 'This user'}
              </div>
              <div>{selectedUser?.email || 'No email available'}</div>
              <div className="mt-2 text-destructive">
                This action cannot be undone.
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false)
                  setSelectedUser(null)
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => selectedUser && handleDeleteUser(selectedUser)}
                disabled={isDeleting === selectedUser?.id}
              >
                {isDeleting === selectedUser?.id ? 'Deleting...' : 'Yes, delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  )
}
